import { useState } from "react";
import { GrIconButton, GrStack, GrTypography, palette } from "@jnpmedi/grebob";
import { resolveFolderFileIcon } from "@newLibs/converting";
import { useBoolean } from "@newHooks/common/useBoolean";
import { useBrowseFoldersQuery } from "@newHooks/query/folder/browseFolders.query";
import { FolderKey } from "@newHooks/query/folder/key";
import { ellipsis } from "@assets/styles/layout";
import { BrowseEntrySummary, BrowseEntryType, FolderType } from "@maven-rest/docs/Folder.dto";
import { compareBigintSafe } from "@maven-helper/functions/Misc.function";

interface FolderFileTreeRowProps {
  organizationId: bigint;
  targetResourceId?: bigint;
  depth: number;
  folderFileResource: BrowseEntrySummary;
  selectedFolderItems: Array<BrowseEntrySummary>;
  parentFolderItems: BrowseEntrySummary[];
  folderType: FolderType;
  onFolderItemSelect: (items: Array<BrowseEntrySummary>) => void;
}

const FOLDER_FILE_TREE_ROW_HEIGHT = 32;
export function FolderFileTreeRow(props: FolderFileTreeRowProps) {
  const {
    organizationId,
    targetResourceId,
    depth,
    folderFileResource,
    selectedFolderItems,
    parentFolderItems,
    folderType,
    onFolderItemSelect,
  } = props;
  const [browseFoldersEntries, setBrowseFoldersEntries] = useState<BrowseEntrySummary[]>([]);
  const isExpand = useBoolean(false);

  const isExpandable = folderFileResource.type === BrowseEntryType.Folder;
  const isTargetResource = !!targetResourceId && compareBigintSafe(targetResourceId, folderFileResource.resource.id);
  const hierarchyPaddingLeft = depth * 16;
  const isSelected = !!selectedFolderItems.filter((el) => compareBigintSafe(el.resource.id, folderFileResource.resource.id)).length;

  useBrowseFoldersQuery(
    {
      organizationId,
      folderType,
      folderId: folderFileResource.resource.id,
    },
    {
      enabled: isExpandable && !isTargetResource && isExpand.value && !browseFoldersEntries.length,
      queryKey: [FolderKey.BrowseFoldersMoveResource],
      onSuccess: (data) => {
        if (data.entries) {
          setBrowseFoldersEntries(data.entries);
        }
      },
    }
  );

  const handleCurrentFolderFileRowClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (folderFileResource.type !== BrowseEntryType.Folder) {
      return;
    }

    if (depth === 0 && !isTargetResource) {
      onFolderItemSelect([...parentFolderItems]);
    }
  };

  const handleChildFolderFileRowClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, childFolderFileEntry: BrowseEntrySummary) => {
    event.preventDefault();
    event.stopPropagation();

    if (compareBigintSafe(targetResourceId, childFolderFileEntry.resource.id) || childFolderFileEntry.type !== BrowseEntryType.Folder) {
      return;
    }

    onFolderItemSelect([...parentFolderItems, childFolderFileEntry]);
  };

  const handleFolderRowExpand = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    isExpand.onToggle();
  };

  return (
    <GrStack
      width="100%"
      minHeight={FOLDER_FILE_TREE_ROW_HEIGHT}
      height="fit-content"
      flexDirection="column"
      gap={6}
      onClick={handleCurrentFolderFileRowClick}
    >
      <GrStack
        gap={4}
        borderRadius="6px"
        height={FOLDER_FILE_TREE_ROW_HEIGHT}
        alignItems="center"
        justifyContent="flex-start"
        padding={`0px ${hierarchyPaddingLeft}px 0px`}
        backgroundColor={`${isSelected ? palette["blue015"] : palette["white"]}`}
      >
        <GrIconButton
          size={14}
          iconType={isExpand.value ? "caret-down-filled" : "caret-right-filled"}
          disabled={isTargetResource || !isExpandable}
          style={{ visibility: isExpandable ? "inherit" : "hidden", userSelect: "none" }}
          onClick={handleFolderRowExpand}
        />
        <img src={resolveFolderFileIcon(folderFileResource)} height="24" alt="Folder" />
        <GrTypography type="Content.Body04" color="grey090" style={ellipsis}>
          {folderFileResource.resource.name}
        </GrTypography>
      </GrStack>
      {/* Child directory section */}
      {isExpand.value &&
        !!browseFoldersEntries.length &&
        browseFoldersEntries.map((entry) => (
          <div key={String(entry.resource.id)} onClick={(event) => handleChildFolderFileRowClick(event, entry)}>
            <FolderFileTreeRow
              organizationId={organizationId}
              depth={depth + 1}
              targetResourceId={targetResourceId}
              selectedFolderItems={selectedFolderItems}
              folderFileResource={entry}
              folderType={folderType}
              parentFolderItems={[...parentFolderItems, entry]}
              onFolderItemSelect={onFolderItemSelect}
            />
          </div>
        ))}
    </GrStack>
  );
}
